import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageHeader } from '../../../components/PageHeader'
import { GameTitle } from '../../../components/GameTitle'
import { useAuth } from '../../../lib/auth'
import { readError } from '../../../lib/errors'
import {
  commitTokyoMetroTrainPhase, fetchTokyoMetroSnapshot, finalizeTokyoMetroScoring,
  moveTokyoMetroMeeple, passTokyoMetroTurn, placeTokyoMetroDisc,
  runTokyoMetroAiTurn, startTokyoMetroGame,
  submitTokyoMetroBid, subscribeTokyoMetroRoom,
} from '../api/rooms'
import { CARD_BY_ID, type ActionType } from '../data/actionCards'
import { ActionCardArt } from '../components/ActionCardArt'
import { StockCardChip as StockCardChipNew } from '../components/StockCardChip'
import { LINES, STATIONS, type StationId } from '../data/map'
import { resolveTrainPhase } from '../engine/trainPhase'
import type { TMSnapshot } from '../data/types'
import { TokyoMetroMapView, reachableStations } from './TokyoMetroMapView'
import { TokyoMetroIncomeTrack } from './TokyoMetroIncomeTrack'
import { useT } from '../../../i18n'

interface Props {
  roomId: string
  onExit: () => void
}

/**
 * Single room orchestrator. Subscribes to realtime; routes to a
 * per-phase sub-view. Actions are fire-and-forget — the server advances
 * phase transitions itself and we pick them up over the subscription.
 */
export function TokyoMetroRoomScreen({ roomId, onExit }: Props) {
  const t = useT()
  const { profile } = useAuth()
  const me = profile?.id ?? null
  const [snap, setSnap] = useState<TMSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)

  const refresh = useCallback(async () => {
    try { setSnap(await fetchTokyoMetroSnapshot(roomId)) }
    catch (e) { setError(readError(e)) }
  }, [roomId])

  useEffect(() => {
    refresh()
    const unsub = subscribeTokyoMetroRoom(roomId, refresh)
    // Polling fallback — realtime subscriptions can drop on flaky
    // networks or stale Postgres publications, leaving the UI frozen
    // on whatever was fetched at mount. A 5s poll keeps the snapshot
    // current even when broadcasts go missing. Stops as soon as the
    // component unmounts.
    const poll = setInterval(refresh, 5000)
    return () => { unsub(); clearInterval(poll) }
  }, [roomId, refresh])

  // Auto-clear errors when the phase advances. Phase transitions
  // happen on a successful action, so by the time we land on a new
  // phase the prior error (e.g. "you already bid this round") is
  // stale and shouldn't keep nagging.
  const lastPhaseRef = useRef<string | null>(null)
  useEffect(() => {
    const phase = snap?.room.phase ?? null
    if (phase && lastPhaseRef.current && lastPhaseRef.current !== phase) {
      setError(null)
    }
    lastPhaseRef.current = phase
  }, [snap?.room.phase])

  const mySeat = useMemo(
    () => snap?.players.find(p => p.profile_id === me) ?? null,
    [snap, me],
  )
  const currentPlayerId = snap?.room
    ? snap.state.turnOrder?.[snap.state.currentPlayerIdx]
    : null
  const itsMyTurn = currentPlayerId != null && mySeat?.id === currentPlayerId

  // ── AI auto-turn driver (sequential phases) ──────────────────────────
  // When the active player is an AI seat, ask the Haiku edge fn what
  // they want to do and fire it. Only one client needs to do this —
  // the tm_* RPCs race-lose safely, so the first to submit wins and
  // the rest silently no-op.
  const aiDrivingRef = useRef(false)
  useEffect(() => {
    if (!snap || aiDrivingRef.current) return
    if (snap.room.status !== 'playing') return
    if (snap.room.phase === 'train' || snap.room.phase === 'refresh'
        || snap.room.phase === 'results' || snap.room.phase === 'turn_order') return
    const current = snap.players.find(p => p.id === currentPlayerId)
    if (!current?.is_ai) return
    aiDrivingRef.current = true
    runTokyoMetroAiTurn(roomId, current.id)
      .catch(e => setError(readError(e)))
      .finally(() => { aiDrivingRef.current = false })
  }, [snap, currentPlayerId, roomId])

  // ── AI auto-bid driver (parallel turn_order phase) ───────────────────
  // Bidding is simultaneous — every seat submits a sealed bid, no
  // "current player". The sequential driver above can't handle this,
  // so we have a separate effect that fires `runTokyoMetroAiTurn` for
  // each AI seat that hasn't yet bid this round. The edge fn returns
  // a `bid` intent and the helper hits tm_submit_bid.
  //
  // We track in-flight ids so the same AI doesn't get triple-fired
  // across realtime ticks before its bid lands in the snapshot.
  const aiBidsInFlightRef = useRef<Set<string>>(new Set())
  useEffect(() => {
    if (!snap) return
    if (snap.room.status !== 'playing') return
    if (snap.room.phase !== 'turn_order') return

    const bids = snap.state.bids ?? []
    const alreadyBid = new Set(bids.map(b => b.playerId))

    for (const player of snap.players) {
      if (!player.is_ai) continue
      if (alreadyBid.has(player.id)) continue
      if (aiBidsInFlightRef.current.has(player.id)) continue
      aiBidsInFlightRef.current.add(player.id)
      runTokyoMetroAiTurn(roomId, player.id)
        .catch(e => console.warn('[tokyo-metro-ai] auto-bid failed:', e))
        .finally(() => { aiBidsInFlightRef.current.delete(player.id) })
    }
  }, [snap, roomId])

  if (!snap) {
    return (
      <div className="screen">
        <PageHeader title="Tokyo Metro" subtitle={t('Connecting…')} onBack={onExit} />
        <div className="flex-1 px-5 py-5 space-y-3">
          <div className="h-24 rounded-2xl bg-brand-elevated animate-pulse" />
          <div className="h-48 rounded-2xl bg-brand-elevated animate-pulse" />
        </div>
      </div>
    )
  }

  const { room } = snap

  // ── Lobby phase (waiting for start) ──────────────────────────────────
  if (room.status === 'lobby') {
    const canStart = snap.players.length >= 2 && room.mode === 'online'
    return (
      <RoomShell snap={snap} onExit={onExit} error={error} onClearError={() => setError(null)}>
        <div className="card p-5 text-center">
          <GameTitle game="tokyo-metro" size="md" />
          <p className="text-sm text-brand-muted mt-2">
            {room.mode === 'matchmade'
              ? t('Waiting for the game to start…')
              : t('Host can start once everyone is seated.')}
          </p>
        </div>
        <div className="floating-actions">
          {room.mode === 'online' && canStart ? (
            <button
              className="btn-primary"
              disabled={busy}
              onClick={async () => {
                setBusy(true); setError(null)
                try { await startTokyoMetroGame(roomId); await refresh() }
                catch (e) { setError(readError(e)) }
                finally { setBusy(false) }
              }}
            >{t('Start Game')}</button>
          ) : (
            <div className="card p-3 text-center text-sm text-brand-muted">
              {snap.players.length < 2 ? t('Need at least 2 players…') : t('Waiting for host…')}
            </div>
          )}
        </div>
      </RoomShell>
    )
  }

  if (room.phase === 'results' || room.status === 'finished') {
    return (
      <RoomShell snap={snap} onExit={onExit} error={error} onClearError={() => setError(null)}>
        <ResultsView snap={snap} roomId={roomId} onRefresh={refresh} />
      </RoomShell>
    )
  }

  // ── Live phases ──────────────────────────────────────────────────────
  const phaseView = (() => {
    if (room.phase === 'turn_order') {
      return <TurnOrderView snap={snap} mySeatId={mySeat?.id ?? null} roomId={roomId} onError={setError} />
    }
    if (room.phase === 'movement') {
      return <MovementView snap={snap} mySeatId={mySeat?.id ?? null}
                           itsMyTurn={itsMyTurn} roomId={roomId} onError={setError} />
    }
    if (room.phase === 'action') {
      return <ActionView snap={snap} mySeatId={mySeat?.id ?? null}
                         itsMyTurn={itsMyTurn} roomId={roomId} onError={setError} />
    }
    if (room.phase === 'train') {
      return <TrainView snap={snap} roomId={roomId} onError={setError} />
    }
    if (room.phase === 'refresh') {
      return (
        <div className="card p-5 text-center">
          <p className="text-sm text-brand-muted">{t('Refreshing action cards…')}</p>
        </div>
      )
    }
    return <div />
  })()

  return (
    <RoomShell snap={snap} onExit={onExit} error={error} onClearError={() => setError(null)}>
      {phaseView}
    </RoomShell>
  )
}

// ── Shell ──────────────────────────────────────────────────────────────

function RoomShell({
  snap, onExit, error, onClearError, children,
}: {
  snap: TMSnapshot
  onExit: () => void
  error: string | null
  onClearError?: () => void
  children: React.ReactNode
}) {
  const t = useT()
  const { room, state } = snap
  return (
    <div className="screen">
      <PageHeader
        title={t('Tokyo Metro · {code}', { code: room.code })}
        subtitle={
          room.status === 'finished' ? t('Game over')
          : room.status === 'lobby' ? t('Lobby')
          : t('Round {n} · {phase}', { n: state.round ?? 1, phase: labelPhase(room.phase, t) })
        }
        onBack={onExit}
      />
      <div className="flex-1 px-5 py-4 space-y-4 overflow-y-auto pb-32">
        {error && (
          <div className="card p-3 bg-brand-danger/10 border border-brand-danger/30
                          flex items-start gap-2">
            <p className="text-sm text-brand-danger flex-1">{error}</p>
            {onClearError && (
              <button
                onClick={onClearError}
                className="text-brand-danger/70 hover:text-brand-danger text-xs px-1"
                aria-label={t('Dismiss')}
              >
                ✕
              </button>
            )}
          </div>
        )}
        {children}
        <PlayerStrip snap={snap} />
        <TokyoMetroIncomeTrack snapshot={snap} />
      </div>
    </div>
  )
}

function labelPhase(p: string, t: (k: string) => string): string {
  switch (p) {
    case 'turn_order': return t('Turn order bid')
    case 'movement':   return t('Movement')
    case 'action':     return t('Action')
    case 'train':      return t('Train phase')
    case 'refresh':    return t('Refresh')
    case 'results':    return t('Results')
    default:           return p
  }
}

// ── Phase views ────────────────────────────────────────────────────────

function TurnOrderView({
  snap, mySeatId, roomId, onError,
}: { snap: TMSnapshot; mySeatId: string | null; roomId: string; onError: (m: string) => void }) {
  const t = useT()
  const [bid, setBid] = useState(0)
  const [busy, setBusy] = useState(false)
  // Optimistic flag — once we've fired submitTokyoMetroBid, hide the
  // input even before the realtime tick brings the bid back in the
  // snapshot. Stops the "double-tap before realtime catches up =
  // already-bid error" race that left the input visible after a
  // successful submit.
  const [optimisticBidLocked, setOptimisticBidLocked] = useState(false)
  const me = snap.players.find(p => p.id === mySeatId) ?? null
  const myBid = snap.state.bids?.find(b => b.playerId === mySeatId) ?? null
  const yen = me?.state.yen ?? 0
  const remaining = snap.players.length - (snap.state.bids?.length ?? 0)

  const showLocked = myBid !== null || optimisticBidLocked

  async function submit() {
    if (busy || !mySeatId) return
    setBusy(true)
    setOptimisticBidLocked(true)
    try {
      await submitTokyoMetroBid(roomId, bid)
    } catch (e) {
      const msg = readError(e)
      // "You already bid this round" is benign here — it means the
      // server already accepted our bid and the realtime tick just
      // hadn't backfilled when the user re-tapped. Don't surface it.
      if (/already bid/i.test(msg)) {
        // Stay in the locked view; realtime will catch up soon.
      } else {
        setOptimisticBidLocked(false)
        onError(msg)
      }
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <TokyoMetroMapView snapshot={snap} />
      <div className="card p-4">
        <p className="text-xs font-semibold text-brand-muted uppercase tracking-wide mb-2">{t('Blind bid for turn order')}</p>
        {showLocked ? (
          <p className="text-sm text-brand-muted">
            {myBid
              ? remaining === 1
                ? t('You bid {yen}¥. Waiting for 1 more bid.', { yen: myBid.yen })
                : t('You bid {yen}¥. Waiting for {n} more bids.', { yen: myBid.yen, n: remaining })
              : t('Bid sent. Waiting for it to register…')
            }
          </p>
        ) : (
          <>
            <p className="text-sm text-brand-muted mb-3">{t('You have')}<strong className="text-brand-text">{yen}¥</strong>.
              Highest bid goes first.
            </p>
            <input
              type="range" min={0} max={yen} value={bid}
              onChange={e => setBid(parseInt(e.target.value, 10))}
              className="w-full"
            />
            <div className="flex items-center justify-between mt-2">
              <span className="font-display text-2xl font-bold text-brand-text">{bid}¥</span>
              <button
                className="btn-primary w-auto px-5 py-2"
                disabled={busy || !mySeatId}
                onClick={submit}
              >
                {busy ? t('Bidding…') : t('Lock in')}
              </button>
            </div>
          </>
        )}
      </div>
    </>
  )
}

function MovementView({
  snap, mySeatId, itsMyTurn, roomId, onError,
}: {
  snap: TMSnapshot; mySeatId: string | null; itsMyTurn: boolean
  roomId: string; onError: (m: string) => void
}) {
  const me = snap.players.find(p => p.id === mySeatId) ?? null
  const myStation = me?.state.meepleStation as StationId | undefined | null
  const steps = 2 + (me?.state.bicycles ?? 0 > 0 ? 1 : 0)
  // Per rulebook pg 8: in round 1, each player places their meeple
  // onto any empty space — no movement-range restriction. From round 2
  // onward, players move up to 2 spaces (3 with bicycle).
  const isPlacement = !myStation

  // Set of station ids occupied by other meeples — can't enter a
  // space with another meeple (rulebook).
  const occupied = useMemo(() => {
    const s = new Set<StationId>()
    for (const p of snap.players) {
      if (p.id === mySeatId) continue
      const sid = p.state.meepleStation as StationId | null | undefined
      if (sid) s.add(sid)
    }
    return s
  }, [snap.players, mySeatId])

  const tappable = useMemo(() => {
    if (!itsMyTurn) return new Set<StationId>()
    if (isPlacement) {
      // Placement: every station that isn't already occupied.
      const all = STATIONS_LIST.filter(id => !occupied.has(id))
      return new Set(all)
    }
    if (!myStation) return new Set<StationId>()
    const reach = reachableStations(myStation, steps)
    // Filter out stations occupied by other meeples.
    for (const id of occupied) reach.delete(id)
    return reach
  }, [itsMyTurn, isPlacement, myStation, occupied, steps])

  const current = snap.players.find(
    p => p.id === snap.state.turnOrder?.[snap.state.currentPlayerIdx],
  )

  return (
    <>
      <div className="card p-3">
        <p className="text-sm text-brand-text">
          {itsMyTurn
            ? isPlacement
              ? <>Your turn: <strong>place your meeple</strong> at any empty station.</>
              : <>Your turn: move up to <strong>{steps}</strong> spaces.</>
            : <>Waiting for <strong>{current?.display_name ?? '?'}</strong> to{' '}
                {(snap.state.round ?? 1) === 1 ? 'place their meeple' : 'move'}.</>}
        </p>
      </div>
      <TokyoMetroMapView
        snapshot={snap}
        activeMeepleStationId={myStation ?? null}
        tappableStations={tappable}
        onTapStation={async (id) => {
          try { await moveTokyoMetroMeeple(roomId, id) }
          catch (e) { onError(readError(e)) }
        }}
      />
    </>
  )
}

// Cached list of all station ids — used by the placement-tappable
// computation in MovementView.
const STATIONS_LIST: StationId[] = STATIONS.map(s => s.id)

function ActionView({
  snap, mySeatId, itsMyTurn, roomId, onError,
}: {
  snap: TMSnapshot; mySeatId: string | null; itsMyTurn: boolean
  roomId: string; onError: (m: string) => void
}) {
  const t = useT()
  const me = snap.players.find(p => p.id === mySeatId) ?? null
  const current = snap.players.find(
    p => p.id === snap.state.turnOrder?.[snap.state.currentPlayerIdx],
  )

  return (
    <>
      <div className="card p-3">
        <p className="text-sm text-brand-text">
          {itsMyTurn
            ? <>Your turn: place an action disc or pass.</>
            : <>Waiting for <strong>{current?.display_name ?? '?'}</strong> to act.</>}
          {' '}You have <strong>{me?.state.discsAvailable ?? 0}</strong> disc(s).
        </p>
      </div>
      <TokyoMetroMapView snapshot={snap} activeMeepleStationId={me?.state.meepleStation ?? null} />
      <ActionCardColumns snap={snap} mySeatId={mySeatId} itsMyTurn={itsMyTurn}
                         roomId={roomId} onError={onError} />
      {itsMyTurn && (
        <div className="floating-actions">
          <button
            className="btn-outline"
            onClick={async () => {
              try { await passTokyoMetroTurn(roomId) }
              catch (e) { onError(readError(e)) }
            }}
          >
            {t('Pass ({n} × 100¥)', { n: me?.state.discsAvailable ?? 0 })}
          </button>
        </div>
      )}
    </>
  )
}

function ActionCardColumns({
  snap, mySeatId, itsMyTurn, roomId, onError,
}: {
  snap: TMSnapshot; mySeatId: string | null; itsMyTurn: boolean
  roomId: string; onError: (m: string) => void
}) {
  const me = snap.players.find(p => p.id === mySeatId) ?? null
  const columns = snap.state.columns ?? []

  return (
    <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
      {columns.map((col, colIdx) => (
        <div key={colIdx} className="space-y-2">
          {col.map(cardId => {
            const card = CARD_BY_ID[cardId]
            if (!card) return null
            return (
              <div key={cardId} className="card p-2">
                {/* Synthesized card front — shows line-pair stripe,
                    action(s), cost, and disc dots. The print art was
                    too brittle to map reliably (and a third of fronts
                    were missing); this renders deterministically from
                    the deck data. */}
                <div className="mb-2 flex justify-center">
                  <ActionCardArt cardId={cardId} size="md" />
                </div>
                <div className="space-y-1">
                  {card.slots.map(slot => {
                    const placement = snap.state.placements?.find(
                      p => p.cardId === cardId && p.slot === slot.slot,
                    )
                    const disabled = !!placement || !itsMyTurn
                      || (me?.state.discsAvailable ?? 0) < slot.discs
                    return (
                      <ActionSlotButton
                        key={slot.slot}
                        actionType={slot.type}
                        cost={slot.cost}
                        discs={slot.discs}
                        value={slot.value}
                        disabled={disabled}
                        placed={!!placement}
                        snap={snap}
                        mySeatId={mySeatId}
                        onPlace={async (params) => {
                          try {
                            await placeTokyoMetroDisc(roomId, {
                              cardId, slot: slot.slot, actionType: slot.type,
                              cost: slot.cost, discs: slot.discs, params,
                            })
                          } catch (e) { onError(readError(e)) }
                        }}
                      />
                    )
                  })}
                </div>
              </div>
            )
          })}
        </div>
      ))}
    </div>
  )
}

/** Stock-card chip — re-export of the synthesized component. */
function StockCardChip({ line, order }: { line: string; order: 1 | 2 | 3 }) {
  return <StockCardChipNew line={line} order={order} size="sm" />
}

/** Single action-slot button with an inline param picker for line-bound actions. */
function ActionSlotButton({
  actionType, cost, discs, value, disabled, placed, snap, mySeatId, onPlace,
}: {
  actionType: ActionType
  cost: number; discs: number; value?: number
  disabled: boolean; placed: boolean
  snap: TMSnapshot; mySeatId: string | null
  onPlace: (params: Record<string, unknown>) => Promise<void>
}) {
  const t = useT()
  const [expanded, setExpanded] = useState(false)
  const needsLine = actionType === 'INVEST' || actionType === 'START' ||
                    actionType === 'SPEED' || actionType === 'SPECULATE'

  const me = snap.players.find(p => p.id === mySeatId)
  const myStation = me?.state.meepleStation

  async function fire(params: Record<string, unknown> = {}) {
    setExpanded(false)
    await onPlace({ value, ...params })
  }

  return (
    <div>
      <button
        disabled={disabled}
        onClick={() => {
          // Simple actions fire immediately. Line-bound + MOVE need params.
          if (actionType === 'MOVE') {
            // MOVE via action card bumps meeple — v1 uses current station
            // (adjacency picking for the action-card MOVE lands in v2).
            fire({ targetStation: myStation })
          } else if (needsLine) {
            setExpanded(x => !x)
          } else {
            fire()
          }
        }}
        className={`w-full p-3 rounded-xl flex items-center justify-between text-left
                    transition-colors
                    ${placed ? 'bg-brand-primary/10 border border-brand-primary/30'
                             : disabled ? 'bg-brand-elevated opacity-60'
                                        : 'bg-white border border-brand-border active:bg-brand-elevated'}`}
      >
        <div>
          <p className="font-semibold text-brand-text">{actionType}</p>
          <p className="text-xs text-brand-muted">
            {cost > 0 ? `${cost}¥ · ` : ''}
            {discs === 1 ? t('{n}×disc', { n: discs }) : t('{n}×discs', { n: discs })}
            {value != null ? ` · ${value}` : ''}
          </p>
        </div>
        {placed && <span className="text-xs text-brand-primary font-semibold">{t('taken')}</span>}
      </button>

      {expanded && needsLine && (
        <div className="mt-1 p-2 bg-brand-elevated rounded-xl flex flex-wrap gap-1.5">
          {LINES.map(l => (
            <button
              key={l.id}
              onClick={() => fire(actionType === 'SPECULATE'
                ? { line: l.id, amount: 300 }
                : { line: l.id })}
              className="px-2 py-1 rounded-md text-xs font-bold text-white"
              style={{ backgroundColor: l.color }}
            >
              {l.id}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}

function TrainView({
  snap, roomId, onError,
}: { snap: TMSnapshot; roomId: string; onError: (m: string) => void }) {
  const t = useT()
  const [committing, setCommitting] = useState(false)
  const [committed, setCommitted] = useState(false)

  useEffect(() => {
    if (committing || committed) return
    // Any player can commit — first one wins (server is idempotent).
    setCommitting(true)
    const result = resolveTrainPhase(snap.state)
    commitTokyoMetroTrainPhase(roomId, result.trains, result.payouts)
      .then(() => setCommitted(true))
      .catch(e => onError(readError(e)))
      .finally(() => setCommitting(false))
  }, [roomId, snap.state, committing, committed, onError])

  return (
    <div className="card p-5 text-center">
      <p className="font-display text-xl font-bold text-brand-text mb-2">{t('Trains running…')}</p>
      <p className="text-sm text-brand-muted">{t('Resolving train movement and station payouts.')}</p>
    </div>
  )
}

function ResultsView({
  snap, roomId, onRefresh,
}: { snap: TMSnapshot; roomId: string; onRefresh: () => void }) {
  const t = useT()
  const [finalized, setFinalized] = useState(false)
  const [busy, setBusy] = useState(false)

  useEffect(() => {
    if (finalized) return
    if (snap.players.some(p => (p.state.finalScore ?? 0) !== 0)) {
      setFinalized(true)
      return
    }
    setBusy(true)
    finalizeTokyoMetroScoring(roomId)
      .then(() => { setFinalized(true); onRefresh() })
      .catch(() => { /* swallow; a rival client may have finalized first */ })
      .finally(() => setBusy(false))
  }, [roomId, snap.players, finalized, onRefresh])

  const sorted = [...snap.players].sort(
    (a, b) => (b.state.finalScore ?? 0) - (a.state.finalScore ?? 0),
  )

  return (
    <div className="card p-5 space-y-3">
      <p className="font-display text-2xl font-black text-brand-text text-center">{t('Final scores')}</p>
      {busy && !finalized && (
        <p className="text-center text-sm text-brand-muted">{t('Computing payouts…')}</p>
      )}
      <ul className="space-y-2">
        {sorted.map((p, i) => (
          <li key={p.id} className="flex items-center gap-3 p-2 rounded-xl bg-white border border-brand-border">
            <span className="font-display text-2xl font-black text-brand-text w-8 text-center">
              {i + 1}
            </span>
            <div className="flex-1 min-w-0">
              <p className="font-semibold text-brand-text truncate">{p.display_name}</p>
              <p className="text-xs text-brand-muted">
                {t('{stocks} stock · {specs} spec', {
                  stocks: p.state.stocks?.length ?? 0,
                  specs: p.state.speculations?.length ?? 0,
                })}
                {p.state.loans?.length ? ` · ${t('{n} loan', { n: p.state.loans.length })}` : ''}
              </p>
            </div>
            <span className="font-display text-xl font-bold text-brand-text">
              {p.state.finalScore ?? 0}¥
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}

// ── Side panels ────────────────────────────────────────────────────────

function PlayerStrip({ snap }: { snap: TMSnapshot }) {
  const t = useT()
  return (
    <div className="flex gap-2 overflow-x-auto pb-1">
      {snap.players.map(p => (
        <div key={p.id} className="card p-2 min-w-[140px] shrink-0">
          <p className="text-xs font-bold text-brand-text truncate">{p.display_name}</p>
          <p className="text-xs text-brand-muted">{p.state.yen ?? 0}¥</p>
          <p className="text-[10px] text-brand-muted">
            {t('{available}/{owned} disc', {
              available: p.state.discsAvailable ?? 0,
              owned: p.state.discsOwned ?? 3,
            })}
          </p>
          {(p.state.stocks?.length ?? 0) > 0 && (
            <div className="mt-1 flex flex-wrap gap-1">
              {p.state.stocks.map((s, i) => (
                <StockCardChip key={i} line={s.line} order={s.order} />
              ))}
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

// (TrainIncomeTable replaced by TokyoMetroIncomeTrack — richer bar
// rendering with per-line colour + speed-token markers.)
